import {Advertisement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/advertisement.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackBASMonitoring, TrackPromotions} from '@aboutyou/tracking';
import {
  BannerImage,
  InfoIconWithLink,
  UnstyledLink,
  mediaTabletAndUp,
  useGetAriaLabelFromUrl,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {STREAM_TOP_SPACING} from '../../backend-services/Header/constants';
import {convertAdvertisementToTile} from '../../shared-deprecated/convertAdvertisementToTile';
import {useABTestAYOBasBanner} from '../AyOutletAbTest/useABTestAYOBasBanner';
import {useSponsoredBannerTrackingKey} from './utils/useSponsoredBannerTrackingKey';

export const AdvertisementBanner: React.FC<{
  advertisement: Advertisement;
}> = ({advertisement}) => {
  const trackPromotionKey = useSponsoredBannerTrackingKey(
    advertisement.promotionTracking?.id,
  );
  const {shouldHideBasBanner, activateABTestAYOBasBanner, isAYOBasBanner} =
    useABTestAYOBasBanner();
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

  useEffect(() => {
    if (isAYOBasBanner(advertisement?.targetUrl?.url)) {
      activateABTestAYOBasBanner();
    }
  }, [activateABTestAYOBasBanner, isAYOBasBanner, advertisement]);

  const advertisementTile = convertAdvertisementToTile(advertisement);

  if (advertisementTile === null) {
    return (
      <TrackBASMonitoring
        basPromotionSource={TrackingPromotionCreative.BasCategoryHeader}
      />
    );
  }

  const {targetUrl, promotionTracking} = advertisementTile;

  if (isAYOBasBanner(targetUrl) && shouldHideBasBanner) {
    return (
      <TrackBASMonitoring
        basPromotionSource={TrackingPromotionCreative.BasCategoryHeader}
      />
    );
  }

  const bannerImage = (
    <StyledBannerImage
      testId="BannerImage"
      url={advertisement.image.url}
      disableFixedRatio
    />
  );

  return (
    <StyledTrackPromotions
      key={trackPromotionKey}
      disabled={!trackPromotionKey}
      promotion={{
        advertisement: {
          promotionId: promotionTracking?.id ?? '',
          id: advertisementTile.id,
          path: targetUrl || '',
          name:
            advertisement.promotionTracking?.name ||
            advertisement.image.description,
        },
        source: TrackingPromotionCreative.BasCategoryHeader,
      }}
      streamPosition={0}
      threshold={0.5}
      triggerOnce
    >
      {targetUrl === undefined ? (
        bannerImage
      ) : (
        <UnstyledLink
          to={targetUrl || ''}
          aria-label={getAriaLabelFromUrl(targetUrl)}
        >
          {bannerImage}
        </UnstyledLink>
      )}
      {advertisement.infoIcon?.cta?.linkTarget.url && (
        <InfoIconWithLink
          to={advertisement.infoIcon.cta.linkTarget.url}
          aria-label={getAriaLabelFromUrl(
            advertisement.infoIcon.cta.linkTarget.url,
          )}
        />
      )}
    </StyledTrackPromotions>
  );
};

const StyledBannerImage = styled(BannerImage)`
  aspect-ratio: 4 / 1.2; // The BAS team usually sets 2400x717 images

  @media ${mediaTabletAndUp} {
    margin-bottom: ${STREAM_TOP_SPACING}px;
    aspect-ratio: 5 / 1;
  }
`;

const StyledTrackPromotions = styled(TrackPromotions)`
  position: relative;
`;
